import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import type { DeepLinkRoute, DeepLinkRouteFormValues } from '../../../models/MasterAdmin/deepLink';
import {
  dlBtnPrimaryClass,
  dlBtnSecondaryClass,
  dlInputClass,
  dlLabelClass,
  dlModalHeaderClass,
  dlModalOverlayClass,
  dlModalPanelClass,
  dlSubLabelClass,
  dlTextareaClass,
} from './deepLinkUi';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: DeepLinkRoute | null;
  onClose: () => void;
  onSubmit: (values: DeepLinkRouteFormValues) => Promise<void>;
};

const DeepLinkRouteFormModal: React.FC<Props> = ({ open, mode, initial, onClose, onSubmit }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<DeepLinkRouteFormValues>({
    slug: '',
    label: '',
    screenName: '',
    appRoute: '',
    webPath: '',
    requiresAuth: true,
    paramSchema: [],
    minAppVersion: '',
    maxAppVersion: '',
    isActive: true,
    sortOrder: 0,
  });
  const [paramJson, setParamJson] = useState('[]');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        slug: initial.slug,
        label: initial.label,
        screenName: initial.screenName || '',
        appRoute: initial.appRoute || '',
        webPath: initial.webPath || '',
        requiresAuth: initial.requiresAuth,
        paramSchema: initial.paramSchema || [],
        minAppVersion: initial.minAppVersion || '',
        maxAppVersion: initial.maxAppVersion || '',
        isActive: initial.isActive,
        sortOrder: initial.sortOrder,
      });
      setParamJson(JSON.stringify(initial.paramSchema || [], null, 2));
    } else {
      setForm({
        slug: '',
        label: '',
        screenName: '',
        appRoute: '',
        webPath: '',
        requiresAuth: true,
        paramSchema: [],
        minAppVersion: '',
        maxAppVersion: '',
        isActive: true,
        sortOrder: 0,
      });
      setParamJson('[]');
    }
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let paramSchema: DeepLinkRouteFormValues['paramSchema'] = [];
      try {
        paramSchema = JSON.parse(paramJson);
      } catch {
        throw new Error('paramSchema must be valid JSON array');
      }
      await onSubmit({
        ...form,
        paramSchema,
        screenName: form.screenName || undefined,
        appRoute: form.appRoute || undefined,
        webPath: form.webPath || undefined,
        minAppVersion: form.minAppVersion || undefined,
        maxAppVersion: form.maxAppVersion || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className={dlModalOverlayClass}>
      <div className={`${dlModalPanelClass} max-w-2xl`}>
        <div className={dlModalHeaderClass}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {mode === 'create' ? 'Add route' : 'Edit route'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 dark:text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className={dlLabelClass}>Slug</span>
              <input
                required
                disabled={mode === 'edit'}
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                className={`${dlInputClass} disabled:bg-gray-100 dark:disabled:bg-gray-800`}
              />
            </label>
            <label className="block text-sm">
              <span className={dlLabelClass}>Label</span>
              <input
                required
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                className={dlInputClass}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className={dlLabelClass}>Screen name (canonical)</span>
            <input
              value={form.screenName}
              onChange={(e) => setForm((p) => ({ ...p, screenName: e.target.value }))}
              className={dlInputClass}
            />
          </label>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className={dlLabelClass}>App route</span>
              <input
                value={form.appRoute}
                onChange={(e) => setForm((p) => ({ ...p, appRoute: e.target.value }))}
                className={dlInputClass}
              />
            </label>
            <label className="block text-sm">
              <span className={dlLabelClass}>Web path</span>
              <input
                value={form.webPath}
                onChange={(e) => setForm((p) => ({ ...p, webPath: e.target.value }))}
                className={dlInputClass}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className={dlLabelClass}>Param schema (JSON)</span>
            <textarea
              value={paramJson}
              onChange={(e) => setParamJson(e.target.value)}
              rows={5}
              className={`${dlTextareaClass} font-mono text-xs`}
            />
          </label>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className={dlLabelClass}>Min app version</span>
              <input
                value={form.minAppVersion}
                onChange={(e) => setForm((p) => ({ ...p, minAppVersion: e.target.value }))}
                className={dlInputClass}
              />
            </label>
            <label className="block text-sm">
              <span className={dlLabelClass}>Max app version</span>
              <input
                value={form.maxAppVersion}
                onChange={(e) => setForm((p) => ({ ...p, maxAppVersion: e.target.value }))}
                className={dlInputClass}
              />
            </label>
          </div>
          <div className="flex gap-4 text-sm text-gray-700 dark:text-gray-200">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.requiresAuth}
                onChange={(e) => setForm((p) => ({ ...p, requiresAuth: e.target.checked }))}
              />
              Requires login
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button type="button" onClick={onClose} className={dlBtnSecondaryClass}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className={dlBtnPrimaryClass}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeepLinkRouteFormModal;
